import React, { useState, useEffect, useRef } from 'react';
import { auditsApi } from '../services/api';
import { User } from '../contexts/AuthContext';
import AuditTemplate from './AuditTemplate';
import AuditDetailModal from './AuditDetailModal';

interface OperatorDashboardProps {
  user: User;
}

const OperatorDashboard: React.FC<OperatorDashboardProps> = ({ user }) => {
  const [audits, setAudits] = useState([]);
  const [availableAudits, setAvailableAudits] = useState([]);
  const [historialAudits, setHistorialAudits] = useState([]);
  const [activeTab, setActiveTab] = useState('current');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showTemplate, setShowTemplate] = useState<number | null>(null);
  const [showDetailModal, setShowDetailModal] = useState<number | null>(null);
  const [showAssignmentAlert, setShowAssignmentAlert] = useState(false);
  const [newAssignment, setNewAssignment] = useState<any>(null);
  const lastAssignedCountRef = useRef(0);

  useEffect(() => {
    loadAudits();
    loadAvailableAudits();
    if (activeTab === 'history') {
      loadHistorial();
    }
    
    // Polling each 5 seconds to check for new assignments
    const interval = setInterval(() => {
      loadAudits();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [activeTab]);
  
  // Monitor for new assignments
  useEffect(() => {
    const myAuditsCount = audits.filter((a: any) => a.operadorId === user.id && a.estado === 'asignada').length;
    const inProgressCount = audits.filter((a: any) => a.operadorId === user.id && a.estado === 'en_proceso').length;
    const assignedAudits = audits.filter((a: any) => a.operadorId === user.id && a.estado === 'asignada');

    // Check for new assignments (even if something was paused)
    if (assignedAudits.length > 0) {
      const latestAssignment = assignedAudits.sort((a, b) => new Date(b.fechaAsignacion || b.createdAt).getTime() - new Date(a.fechaAsignacion || a.createdAt).getTime())[0];

      // Show alert if:
      // 1. There are assigned audits AND
      // 2. This assignment was made by admin/supervisor (has asignadoPor field)
      const assignmentTime = new Date(latestAssignment.fechaAsignacion || latestAssignment.createdAt).getTime();
      const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000); // Extender a 30 minutos

      // Detectar asignaciones por admin/supervisor (prioritarias)
      // Solo es asignación si asignadoPor es diferente al operador (no auto-asignación)
      const isAdminAssigned = latestAssignment.asignadoPor != null &&
                             latestAssignment.asignadoPor !== latestAssignment.operadorId;
      const isRecent = assignmentTime > thirtyMinutesAgo;

      if (isAdminAssigned && !showAssignmentAlert) {
        console.log('🚨 NUEVA ASIGNACIÓN DETECTADA:', {
          titulo: latestAssignment.titulo,
          asignadaPorAdmin: isAdminAssigned,
          esReciente: isRecent,
          fechaAsignacion: latestAssignment.fechaAsignacion
        });

        // Mostrar alerta prominente FORZADA que no se puede ignorar
        setShowAssignmentAlert(true); // Activar estado de alerta

        // CAMBIAR TÍTULO DEL NAVEGADOR para llamar la atención
        const originalTitle = document.title;
        document.title = "🚨 NUEVA AUDITORÍA ASIGNADA 🚨";

        // Notificación del navegador si está permitida
        if ('Notification' in window) {
          if (Notification.permission === 'granted') {
            new Notification('🚨 NUEVA AUDITORÍA ASIGNADA', {
              body: `"${latestAssignment.titulo}" - Tu trabajo actual se pausó automáticamente`,
              icon: '/favicon.ico'
            });
          } else if (Notification.permission !== 'denied') {
            Notification.requestPermission().then(permission => {
              if (permission === 'granted') {
                new Notification('🚨 NUEVA AUDITORÍA ASIGNADA', {
                  body: `"${latestAssignment.titulo}" - Tu trabajo actual se pausó automáticamente`,
                  icon: '/favicon.ico'
                });
              }
            });
          }
        }

        // ASIGNACIÓN tiene PRIORIDAD - Sonido + Cartel obligatorio
        console.log(`🔄 Nueva auditoría asignada: ${latestAssignment.titulo}`);

        // Reproducir sonido de notificación
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmYaACCVz/LNeSsFJHTR8N6QQAoUXrTp66hVFApGn+DyvmYaACCVz/LNeSsFJHTR8N6QQAoUXrTp66hVFApGn+DyvmYaACCVz/LNeSsFJHTR8N6QQAoUXrTp66hVFApGn+DyvmYaACCVz/LNeSsFJHTR8N6QQAoUXrTp66hVFApGn+DyvmYaACCVz/LNeSsFJHTR8N6QQAoUXrTp66hVFApGn+DyvmYaACCVz/LNeSsFJHTR8N6QQAo=');
          audio.play().catch(e => console.log('Audio notification not available'));
        } catch (e) {
          console.log('Audio notification not supported');
        }

        // Cartel para ASIGNACIONES (tiene prioridad)
        alert(`🚨 NUEVA AUDITORÍA ASIGNADA 🚨\n\n` +
              `Por: ${isAdminAssigned ? 'SUPERVISOR/ADMIN' : 'SISTEMA'}\n` +
              `Auditoría: "${latestAssignment.titulo}"\n\n` +
              `⏸️ Tu trabajo actual se pausó automáticamente\n` +
              `✅ Debes empezar la nueva auditoría ahora`);

        // Notificación del navegador (si está permitida)
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Nueva Auditoría Asignada', {
            body: `"${latestAssignment.titulo}" - Por ${isAdminAssigned ? 'SUPERVISOR/ADMIN' : 'SISTEMA'}`,
            icon: '/favicon.ico'
          });
        }

        // Restaurar título después de 30 segundos
        setTimeout(() => {
          document.title = originalTitle;
        }, 30000);

        // Tomar automáticamente la auditoría asignada
        takeAssignedAudit(latestAssignment.id);
      }
    }

    lastAssignedCountRef.current = myAuditsCount;
  }, [audits, user.id, showAssignmentAlert]);

  const loadAudits = async () => {
    try {
      let response;

      // Both supervisors and operators use the same endpoint in this dashboard
      // Supervisors function exactly like operators in this section and can take audits for payment
      response = await auditsApi.getAll({ all: 'true' });

      if (response.data.success) {
        // Handle different response structures
        const auditsData = response.data.auditorias || response.data.data || [];
        setAudits(auditsData);
      }
    } catch (err) {
      console.error('Error cargando auditorías:', err);
    }
  };

  const loadAvailableAudits = async () => {
    try {
      // Use 'available=true' to get pending audits that are not assigned to any operator
      const response = await auditsApi.getAll({ available: 'true' });
      if (response.data.success) {
        setAvailableAudits(response.data.data);
      }
    } catch (err) {
      console.error('Error cargando auditorías disponibles:', err);
    }
  };

  const loadHistorial = async () => {
    try {
      const response = await auditsApi.getOperatorHistory();
      if (response.data.success) {
        setHistorialAudits(response.data.data);
      }
    } catch (err) {
      console.error('Error cargando historial:', err);
    }
  };

  const takeAudit = async (auditId: number) => {
    if (loading) return;
    setLoading(true);
    setError('');
    // No setSuccess - debe ser silencioso para tomas normales

    try {
      const response = await auditsApi.take(auditId);
      if (response.data.success) {
        // SIN cartel al tomar auditoría - solo log silencioso v2
        console.log('✅ Auditoría tomada manualmente (v2):', response.data.data?.titulo || auditId);

        // Remove the taken audit from available audits immediately
        const takenAudit = availableAudits.find((audit: any) => audit.id === auditId);
        setAvailableAudits(prev => prev.filter((audit: any) => audit.id !== auditId));

        // Add the taken audit to my audits list with updated status
        if (takenAudit) {
          const updatedAudit = {
            ...takenAudit,
            estado: 'asignada',
            operadorId: user.id,
            esMiTrabajoComoOperador: true
          };
          setAudits(prev => [updatedAudit, ...prev]);
        }

        loadAudits();
        loadAvailableAudits();
      }
    } catch (err: any) {
      setError('Error al tomar auditoría: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const takeAssignedAudit = async (auditId: number) => {
    if (loading) return;
    setLoading(true);
    setError('');
    // No setSuccess - debe ser silencioso para tomas de asignadas

    try {
      // Usar el endpoint especial para auditorías asignadas por admin
      const response = await auditsApi.takeAssigned(auditId);

      if (response.data.success) {
        // Solo log silencioso - SIN carteles cuando se toma automáticamente
        console.log('✅ Auditoría tomada automáticamente:', response.data.data.titulo);
        setShowAssignmentAlert(false); // Cerrar cualquier alert que esté abierto

        // Recargar datos para reflejar cambios
        loadAudits();
        loadAvailableAudits();

      }
    } catch (err: any) {
      setError('Error al tomar auditoría asignada: ' + (err.response?.data?.error || err.message));
      console.error('❌ Error tomando auditoría asignada:', err);
    } finally {
      setLoading(false);
    }
  };

  const startAudit = async (auditId: number) => {
    setLoading(true);
    setError('');
    // No setSuccess - debe ser silencioso para inicios
    
    try {
      const response = await auditsApi.start(auditId);
      
      if (response.data.success) {
        // SIN cartel al iniciar auditoría - solo proceder
        await loadAudits();

        // Mostrar plantilla de auditoría
        setShowTemplate(auditId);
      } else {
        setError('Error: ' + (response.data.message || 'No se pudo iniciar'));
      }
    } catch (err: any) {
      console.error('❌ ERROR START:', err);
      console.error('❌ ERROR RESPONSE:', err.response);
      setError('Error al iniciar: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateComplete = async (auditId: number, data: any) => {

    try {
      const dataToSend = {
        eventos: data.eventos,
        observacionesFinales: data.observacionesFinales,
        tiempoTrabajado: data.tiempoTrabajado,
        sucursalDetails: data.sucursalDetails,
        canalesVision: data.canalesVision,
        estado: data.estado || 'terminada' // Respetar el estado enviado desde el template
      };

      const response = await auditsApi.complete(auditId, dataToSend);

      if (response.data.success) {
        console.log('✅ Auditoría completada:', response.data.message || 'Auditoría procesada exitosamente');
        setShowTemplate(null); // Esto cierra el modal
        await loadAudits();
      }
    } catch (err: any) {
      setError('Error al completar: ' + (err.response?.data?.error || err.message));
    }
  };

  const downloadFile = async (auditId: number, filename: string) => {
    try {
      await auditsApi.downloadFile(auditId, filename);
    } catch (err: any) {
      setError('Error descargando archivo: ' + (err.message || 'Error desconocido'));
    }
  };


  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  // Debug logs temporales
  
  // Debug archivos adjuntos
  audits.forEach((audit: any) => {
  });

  // Lógica de cola:
  // - Operador ve solo la primera auditoría (más prioritaria/antigua)
  // - Supervisor ve todas sus auditorías asignadas
  const myAudits = audits.filter((a: any) => a.operadorId === user.id);

  // 🎯 ORDENAR: ASIGNACIONES FORZADAS PRIMERO (urgentes), luego resto por antigüedad (más viejas primero)
  const myAuditsOrdered = myAudits.sort((a: any, b: any) => {
    // Detectar asignaciones forzadas (asignadoPor diferente al operador)
    const aIsForced = a.asignadoPor != null && a.asignadoPor !== a.operadorId;
    const bIsForced = b.asignadoPor != null && b.asignadoPor !== b.operadorId;

    // Si una es asignación forzada y la otra no, la forzada va primera
    if (aIsForced && !bIsForced) return -1;
    if (bIsForced && !aIsForced) return 1;

    // Si ambas son forzadas o ambas normales, ordenar por fecha (más nueva primero)
    const dateA = new Date(a.fechaCreacion || a.createdAt);
    const dateB = new Date(b.fechaCreacion || b.createdAt);
    return dateB.getTime() - dateA.getTime();
  });

  // 🔧 MOSTRAR TODAS las auditorías para que los contadores sean correctos
  // Pero en la interfaz, el operador trabajará con la más prioritaria
  const nextAudit = myAuditsOrdered; // Tanto supervisor como operador ven todas para contadores correctos

  const assigned = nextAudit.filter((a: any) => a.estado === 'asignada');
  const inProgress = nextAudit.filter((a: any) => a.estado === 'en_proceso');
  const paused = nextAudit.filter((a: any) => a.estado === 'pausada');
  const completed = audits.filter((a: any) => a.estado === 'terminada' && (a.operadorId === user.id || (user.role === 'supervisor' && a.esMiTrabajoComoOperador)));


  return (
    <div style={{ padding: 20 }}>
      <h2>Panel de Operador</h2>

      {/* Pestañas */}
      <div style={{ marginBottom: 20, borderBottom: '1px solid #ddd' }}>
        <button
          onClick={() => setActiveTab('current')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: activeTab === 'current' ? '#007bff' : 'transparent',
            color: activeTab === 'current' ? 'white' : '#007bff',
            cursor: 'pointer',
            marginRight: 10,
            borderRadius: '5px 5px 0 0'
          }}
        >
          Auditorías Actuales
        </button>
        <button
          onClick={() => setActiveTab('history')}
          style={{
            padding: '10px 20px',
            border: 'none',
            background: activeTab === 'history' ? '#007bff' : 'transparent',
            color: activeTab === 'history' ? 'white' : '#007bff',
            cursor: 'pointer',
            borderRadius: '5px 5px 0 0'
          }}
        >
          Historial Completo
        </button>
      </div>

      {error && (
        <div style={{
          background: '#fee',
          color: '#c33',
          padding: '8px 12px',
          borderRadius: 4,
          marginBottom: 16,
          border: '1px solid #fcc',
          cursor: 'pointer'
        }} onClick={clearMessages}>
          {error} (click para cerrar)
        </div>
      )}

      {success && (
        <div style={{
          background: '#efe',
          color: '#373',
          padding: '8px 12px',
          borderRadius: 4,
          marginBottom: 16,
          border: '1px solid #cfc',
          cursor: 'pointer'
        }} onClick={clearMessages}>
          {success} (click para cerrar)
        </div>
      )}

      {activeTab === 'current' && (
        <>
          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            <StatBox label="✔️ Completadas" value={completed.length} />
            <StatBox label="🔄 En Progreso" value={inProgress.length} color="#28a745" />
            <StatBox label="⏸️ Pausadas" value={paused.length} color="#ffc107" />
            <StatBox label="📋 Asignadas" value={assigned.length} color="#17a2b8" />
            <StatBox label="🎯 Disponibles" value={availableAudits.length} color="#007bff" />
          </div>

      <div style={{ marginBottom: 24 }}>
        <button
          onClick={loadAvailableAudits}
          disabled={loading}
          style={{
            background: '#6c757d',
            color: '#fff',
            border: 'none',
            padding: '10px 16px',
            borderRadius: 4,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          🔄 Actualizar Disponibles
        </button>
      </div>

      {/* Auditorías Disponibles - Solo mostrar la primera (más antigua) */}
      <h3>🎯 Próxima Auditoría en Cola ({availableAudits.length > 0 ? 1 : 0})</h3>
      {(() => {
        // Ordenar auditorías disponibles por fecha de creación (más viejas primero)
        const sortedAvailable = [...availableAudits].sort((a: any, b: any) => {
          const dateA = new Date(a.fechaCreacion || a.createdAt);
          const dateB = new Date(b.fechaCreacion || b.createdAt);
          return dateA.getTime() - dateB.getTime();
        });
        return sortedAvailable.length > 0;
      })() ? (
        <div style={{
          background: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: 6,
          padding: 16,
          marginBottom: 24
        }}>
          {(() => {
            // Usar el mismo ordenamiento para mostrar la más vieja
            const sortedAvailable = [...availableAudits].sort((a: any, b: any) => {
              const dateA = new Date(a.fechaCreacion || a.createdAt);
              const dateB = new Date(b.fechaCreacion || b.createdAt);
              return dateA.getTime() - dateB.getTime();
            });
            return sortedAvailable.slice(0, 1);
          })().map((audit: any) => (
            <div
              key={audit.id}
              style={{
                marginBottom: 16,
                padding: '16px',
                background: 'white',
                borderRadius: 6,
                border: '1px solid #bbdefb'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <strong>🏢 {audit.client?.nombre} - {audit.branch?.nombre}</strong>
                  <br />
                  <small style={{ color: '#666' }}>
                    📅 Fecha: {new Date(audit.fechaSolicitada).toLocaleDateString('es-AR', {
                      timeZone: 'America/Argentina/Buenos_Aires'
                    })}
                    <br />
                    🎯 Prioridad: <span style={{ 
                      color: audit.prioridad === 'normal' ? '#059669' : 
                             audit.prioridad === 'prioritaria' ? '#d97706' : '#dc2626' 
                    }}>
                      {audit.prioridad === 'normal' ? '🟢 Normal' : 
                       audit.prioridad === 'prioritaria' ? '🟡 Prioritaria' : '🔴 Urgente'}
                    </span>
                    <br />
                    {audit.observacionesIniciales && (
                      <>
                        <br />
                        📝 Observaciones: {audit.observacionesIniciales}
                      </>
                    )}
                    
                    {audit.observacionesOperador && (
                      <>
                        <br />
                        <div style={{ 
                          background: '#fff3cd', 
                          border: '1px solid #ffeeba', 
                          padding: '0.5rem', 
                          borderRadius: '4px',
                          marginTop: '0.5rem'
                        }}>
                          💬 <strong>Mensajes para ti:</strong><br />
                          {audit.observacionesOperador.split('\n').map((linea, idx) => (
                            <div key={idx} style={{ marginTop: idx > 0 ? '0.25rem' : 0, fontSize: '0.875rem' }}>
                              {linea}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Archivos adjuntos para auditorías disponibles */}
                    {(() => {
                      let archivos = [];
                      try {
                        archivos = typeof audit.archivosAdjuntos === 'string' 
                          ? JSON.parse(audit.archivosAdjuntos || '[]')
                          : (audit.archivosAdjuntos || []);
                      } catch (e) {
                        archivos = [];
                      }
                      return archivos.length > 0 ? (
                        <>
                          <br />
                          📎 <strong>Archivos adjuntos ({archivos.length}):</strong>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                            {archivos.map((archivo: any, index: number) => (
                              <span
                                key={index}
                                style={{
                                  background: '#007bff',
                                  color: 'white',
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  fontSize: '0.75rem'
                                }}
                              >
                                📄 {archivo.nombreOriginal}
                              </span>
                            ))}
                          </div>
                        </>
                      ) : null;
                    })()}
                  </small>
                </div>
                <button
                  onClick={() => takeAudit(audit.id)}
                  disabled={
                    loading ||
                    (user.role !== 'supervisor' && (assigned.length > 0 || inProgress.length > 0))
                  }
                  style={{
                    background: (loading || (user.role !== 'supervisor' && (assigned.length > 0 || inProgress.length > 0)))
                      ? '#6c757d'
                      : (audit.prioridad === 'urgente' ? '#dc3545' : '#28a745'),
                    color: '#fff',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 4,
                    cursor: (loading || (user.role !== 'supervisor' && (assigned.length > 0 || inProgress.length > 0))) ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    opacity: (loading || (user.role !== 'supervisor' && (assigned.length > 0 || inProgress.length > 0))) ? 0.6 : 1
                  }}
                >
                  {loading ? '⏳' : (audit.prioridad === 'urgente' ? '🚨 Tomar Urgente' : '✅ Tomar Auditoría')}
                </button>
              </div>
              {user.role !== 'supervisor' && (assigned.length > 0 || inProgress.length > 0) && (
                <small style={{ color: '#dc3545', fontStyle: 'italic' }}>
                  ⚠️ Finaliza tu auditoría activa primero
                  {assigned.length > 0 && ` (${assigned.length} asignada${assigned.length > 1 ? 's' : ''})`}
                  {inProgress.length > 0 && ` (${inProgress.length} en progreso)`}
                </small>
              )}
              {paused.length > 0 && (
                <small style={{ color: '#6b7280', fontStyle: 'italic' }}>
                  ℹ️ Tienes ${paused.length} auditoría${paused.length > 1 ? 's' : ''} pausada${paused.length > 1 ? 's' : ''}. Puedes tomar auditorías adicionales.
                </small>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          background: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: 6,
          padding: 20,
          textAlign: 'center',
          color: '#6c757d',
          marginBottom: 24
        }}>
          📭 No hay auditorías disponibles en este momento
        </div>
      )}

      {/* Auditorías Asignadas */}
      {assigned.length > 0 && (
        <>
          <h3>📋 {user.role === 'supervisor' ? `Auditorías Asignadas (${assigned.length})` : 'Auditoría Asignada (Prioritaria)'}</h3>
          <div style={{
            background: '#e8f4fd',
            border: '1px solid #17a2b8',
            borderRadius: 6,
            padding: 16,
            marginBottom: 24
          }}>
            {assigned.map((audit: any) => (
              <div key={audit.id} style={{
                marginBottom: 16,
                padding: '16px',
                background: 'white',
                borderRadius: 6,
                border: '1px solid #bee5eb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <strong>🏢 {audit.client?.nombre} - {audit.branch?.nombre}</strong>
                    <br />
                    <small style={{ color: '#666' }}>
                      📅 Asignada: {new Date(audit.fechaAsignacion).toLocaleDateString()}
                      <br />
                      🎯 Prioridad: {audit.prioridad}
                      
                      {audit.observacionesOperador && (
                        <>
                          <br /><br />
                          <div style={{ 
                            background: '#fff3cd', 
                            border: '1px solid #ffeeba', 
                            padding: '0.5rem', 
                            borderRadius: '4px',
                            marginTop: '0.5rem'
                          }}>
                            💬 <strong>Mensajes para ti:</strong><br />
                            {audit.observacionesOperador.split('\n').map((linea, idx) => (
                              <div key={idx} style={{ marginTop: idx > 0 ? '0.25rem' : 0, fontSize: '0.8rem' }}>
                                {linea}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      
                      {/* Archivos adjuntos para auditorías asignadas */}
                      {(() => {
                        let archivos = [];
                        try {
                          archivos = typeof audit.archivosAdjuntos === 'string' 
                            ? JSON.parse(audit.archivosAdjuntos || '[]')
                            : (audit.archivosAdjuntos || []);
                        } catch (e) {
                          archivos = [];
                        }
                        return archivos.length > 0 ? (
                          <>
                            <br />
                            📎 <strong>Archivos ({archivos.length}):</strong>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                              {archivos.map((archivo: any, index: number) => (
                                <span
                                  key={index}
                                  style={{
                                    background: '#007bff',
                                    color: 'white',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  📄 {archivo.nombreOriginal}
                                </span>
                              ))}
                            </div>
                          </>
                        ) : null;
                      })()}
                    </small>
                  </div>
                  <button
                    onClick={() => startAudit(audit.id)}
                    style={{
                      background: '#007bff',
                      color: '#fff',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 14
                    }}
                  >
                    ▶️ Iniciar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Auditorías en Progreso */}
      {inProgress.length > 0 && (
        <>
          <h3>🔄 Auditorías en Progreso ({inProgress.length})</h3>
          <div style={{
            background: '#f8f9fa',
            border: '1px solid #28a745',
            borderRadius: 6,
            padding: 16,
            marginBottom: 24
          }}>
            {inProgress.map((audit: any) => (
              <div key={audit.id} style={{
                marginBottom: 16,
                padding: '16px',
                background: 'white',
                borderRadius: 6,
                border: '1px solid #c3e6cb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <strong>🏢 {audit.client?.nombre} - {audit.branch?.nombre}</strong>
                    {audit.requiereRevision && (
                      <span style={{
                        background: '#dc3545',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        marginLeft: '8px',
                        fontWeight: 'bold'
                      }}>
                        ⚠️ REQUIERE CORRECCIÓN
                      </span>
                    )}
                    <br />
                    <small style={{ color: '#666' }}>
                      📅 Iniciada: {new Date(audit.fechaInicio).toLocaleDateString()}
                      <br />
                      🎯 Prioridad: {audit.prioridad}
                      {audit.descripcion && (
                        <>
                          <br />
                          📋 <strong>Descripción:</strong> {audit.descripcion}
                        </>
                      )}
                      {audit.observacionesIniciales && (
                        <>
                          <br />
                          📝 Obs. iniciales: {audit.observacionesIniciales}
                        </>
                      )}
                      
                      {/* Mostrar motivo de rechazo si fue devuelta */}
                      {audit.requiereRevision && audit.motivoRechazo && (
                        <>
                          <br /><br />
                          <div style={{ 
                            background: '#f8d7da', 
                            border: '1px solid #f5c6cb', 
                            padding: '0.5rem', 
                            borderRadius: '4px',
                            marginTop: '0.5rem'
                          }}>
                            ❌ <strong>Motivo de devolución:</strong><br />
                            <div style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                              {audit.motivoRechazo}
                            </div>
                          </div>
                        </>
                      )}
                      
                      {/* Observaciones del supervisor si existen */}
                      {audit.observacionesSupervisor && (
                        <>
                          <br /><br />
                          <div style={{ 
                            background: '#f8d7da', 
                            border: '1px solid #f5c6cb', 
                            padding: '0.5rem', 
                            borderRadius: '4px',
                            marginTop: '0.5rem'
                          }}>
                            👁️ <strong>Observaciones del supervisor:</strong><br />
                            <div style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                              {audit.observacionesSupervisor}
                            </div>
                          </div>
                        </>
                      )}
                      
                      {audit.observacionesOperador && (
                        <>
                          <br /><br />
                          <div style={{ 
                            background: '#fff3cd', 
                            border: '1px solid #ffeeba', 
                            padding: '0.5rem', 
                            borderRadius: '4px',
                            marginTop: '0.5rem'
                          }}>
                            💬 <strong>Mensajes para ti:</strong><br />
                            {audit.observacionesOperador.split('\n').map((linea, idx) => (
                              <div key={idx} style={{ marginTop: idx > 0 ? '0.25rem' : 0, fontSize: '0.8rem' }}>
                                {linea}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </small>
                    
                    {/* Archivos adjuntos */}
                    {audit.archivosAdjuntos && audit.archivosAdjuntos.length > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        <strong style={{ fontSize: '0.9rem', color: '#495057' }}>📎 Archivos para descargar ({audit.archivosAdjuntos.length}):</strong>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                          {audit.archivosAdjuntos.map((archivo: any, index: number) => (
                            <button
                              key={index}
                              onClick={() => downloadFile(audit.id, archivo.nombreArchivo)}
                              style={{
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                padding: '4px 8px',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                              title={`Descargar ${archivo.nombreOriginal}`}
                            >
                              📄 {archivo.nombreOriginal}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                    <button
                      onClick={() => setShowTemplate(audit.id)}
                      style={{
                        background: '#28a745',
                        color: '#fff',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 14
                      }}
                    >
                      🚀 Continuar Auditoría
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Historial Completadas */}
      {/* Auditorías Pausadas */}
      {paused.length > 0 && (
        <>
          <h3>⏸️ Auditorías Pausadas ({paused.length})</h3>
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: 6,
            padding: 16,
            marginBottom: 24
          }}>
            {paused.map((audit: any) => (
              <div key={audit.id} style={{
                marginBottom: 16,
                padding: '16px',
                background: 'white',
                borderRadius: 6,
                border: '1px solid #ffeeba'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <strong>🏢 {audit.client?.nombre} - {audit.branch?.nombre}</strong>
                    <br />
                    <small style={{ color: '#666' }}>
                      ⏸️ <strong>Pausada</strong> | 🎯 Prioridad: {audit.prioridad}
                      {audit.descripcion && (
                        <>
                          <br />
                          📋 {audit.descripcion}
                        </>
                      )}
                      
                      {/* Mostrar razón de la pausa */}
                      {audit.observacionesOperador && (
                        <div style={{ 
                          background: '#fff3cd', 
                          border: '1px solid #ffeeba',
                          borderRadius: '4px',
                          padding: '8px',
                          marginTop: '8px'
                        }}>
                          💬 <strong>Razón de la pausa:</strong>
                          <div style={{ fontSize: '0.875rem', marginTop: '4px' }}>
                            {audit.observacionesOperador.split('\n').pop()} {/* Última línea = razón de pausa */}
                          </div>
                        </div>
                      )}
                    </small>
                  </div>
                  <button 
                    onClick={() => setShowTemplate(audit.id)}
                    style={{
                      background: '#ffc107',
                      color: '#212529',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 'bold'
                    }}
                  >
                    ▶️ Reanudar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}


      {completed.length > 0 && (
        <>
          <h3>✔️ Auditorías Completadas ({completed.length})</h3>
          <div style={{
            background: '#f0f8f0',
            border: '1px solid #c3e6c3',
            borderRadius: 6,
            padding: 16,
            maxHeight: 400,
            overflowY: 'auto'
          }}>
            {completed.map((audit: any) => (
              <div key={audit.id} style={{
                marginBottom: 12,
                padding: '12px',
                background: 'white',
                borderRadius: 6,
                border: '1px solid #e0f2e0'
              }}>
                <strong>🏢 {audit.client?.nombre} - {audit.branch?.nombre}</strong>
                <br />
                <small style={{ color: '#666' }}>
                  📅 Completada: {new Date(audit.fechaTerminacion).toLocaleDateString()}
                  <br />
                  🎯 Prioridad: {audit.prioridad}
                </small>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Modal de detalles de auditoría */}
      {showDetailModal && (
        <AuditDetailModal
          auditId={showDetailModal}
          onClose={() => setShowDetailModal(null)}
          onStartAudit={() => {
            setShowDetailModal(null);
            setShowTemplate(showDetailModal);
          }}
        />
      )}

        </>
      )}

      {/* Pestaña de Historial */}
      {activeTab === 'history' && (
        <div>
          <h3>📋 Historial Completo ({historialAudits.length} auditorías)</h3>
          {historialAudits.length === 0 ? (
            <p>No se encontraron auditorías en el historial.</p>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {historialAudits.map((audit: any) => (
                <div key={audit.id} style={{
                  border: '1px solid #ddd',
                  padding: 16,
                  borderRadius: 6,
                  background: audit.estado === 'terminada' ? '#f8f9fa' : 
                             audit.estado === 'enviada_cliente' ? '#e7f3ff' :
                             audit.estado === 'aprobada' ? '#d4edda' : '#fff'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h4 style={{ margin: 0, color: '#333' }}>
                      #{audit.id} - {audit.cliente} | {audit.titulo}
                    </h4>
                    <span style={{
                      background: audit.estado === 'terminada' ? '#6c757d' : 
                                 audit.estado === 'enviada_cliente' ? '#007bff' :
                                 audit.estado === 'aprobada' ? '#28a745' : '#ffc107',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      textTransform: 'uppercase'
                    }}>
                      {audit.estado?.replace('_', ' ')}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>
                    <strong>Sucursal:</strong> {audit.sucursal} | <strong>Localidad:</strong> {audit.localidad}
                  </div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    <strong>Creada:</strong> {new Date(audit.createdAt).toLocaleDateString()} | 
                    {audit.fechaTerminacion && (
                      <span> <strong>Terminada:</strong> {new Date(audit.fechaTerminacion).toLocaleDateString()}</span>
                    )}
                  </div>
                  {audit.observacionesOperador && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 8, fontStyle: 'italic' }}>
                      <strong>Observaciones:</strong> {audit.observacionesOperador}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal de alerta de nueva asignación */}
      {showAssignmentAlert && newAssignment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            padding: 24,
            borderRadius: 8,
            maxWidth: 500,
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ color: '#dc3545', marginTop: 0 }}>🚨 Nueva Auditoría Asignada</h3>
            <p>
              <strong>Cliente:</strong> {newAssignment.client?.nombre}<br/>
              <strong>Sucursal:</strong> {newAssignment.branch?.nombre}<br/>
              <strong>Prioridad:</strong> <span style={{ color: newAssignment.prioridad === 'urgente' ? '#dc3545' : '#ffc107' }}>
                {newAssignment.prioridad === 'urgente' ? '🔴 URGENTE' : '🟡 Prioritaria'}
              </span>
            </p>
            
            {newAssignment.observacionesOperador && (
              <div style={{ 
                background: '#fff3cd', 
                border: '1px solid #ffeeba', 
                padding: '12px', 
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                <strong>💬 Mensaje del supervisor:</strong><br/>
                {newAssignment.observacionesOperador}
              </div>
            )}
            
            <p style={{ color: '#666', fontSize: 14 }}>
              Tienes una auditoría en progreso. ¿Qué deseas hacer?
            </p>
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAssignmentAlert(false);
                  setNewAssignment(null);
                }}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              >
                ⏳ Terminar actual primero
              </button>
              <button
                onClick={() => {
                  setShowAssignmentAlert(false);
                  setNewAssignment(null);
                  // Navigate to assigned audit (it will appear in the queue)
                  setActiveTab('current');
                }}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                🚨 Pausar y hacer nueva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de plantilla de auditoría */}
      {showTemplate && (
        <AuditTemplate
          auditId={showTemplate}
          onClose={() => setShowTemplate(null)}
          onComplete={(data) => handleTemplateComplete(showTemplate, data)}
          user={user}
          supervisorMode={false}
        />
      )}
    </div>
  );
};

function StatBox({ label, value, color = '#2e2e2e' }: { label: string; value: number; color?: string }) {
  return (
    <div style={{
      flex: 1,
      background: color,
      padding: 16,
      borderRadius: 6,
      color: '#fff',
      textAlign: 'center',
      minWidth: 150
    }}>
      <h3 style={{ margin: '0 0 8px 0', fontSize: 16 }}>{label}</h3>
      <p style={{ margin: 0, fontSize: 28, fontWeight: 'bold' }}>{value}</p>
    </div>
  );
}

export default OperatorDashboard;